// Settings dropdown (gear icon) shown in the workspace-scope sidebar header,
// extracted from Sidebar.tsx during the P5 split. Pure render: every input
// is a prop.

import type { Ref } from "react";
import {
  GearSix,
  UserPlus,
  PlugsConnected,
  Plugs,
  SignOut,
} from "@/components/ui/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface WorkspaceSettingsMenuProps {
  /** Ref to the trigger — used by `sidebar.toggleNewMenu` to open/close. */
  triggerRef?: Ref<HTMLButtonElement>;
  /** Portal target — keeps the menu inside the sidebar no-drag subtree. */
  portalContainer?: HTMLElement | null;
  setShowWorkspaceSettings: (open: boolean) => void;
  showCoworkers: boolean | undefined;
  onToggleCoworkers: (() => void) | undefined;
  /**
   * Whether the user may reach workspace-management surfaces (settings,
   * member directory, skills, connected tools). False for a non-admin member
   * of a PUBLIC workspace — for them the first item becomes "Leave workspace"
   * (the settings dialog itself renders a Leave-only view) and the management
   * entries are hidden. Mirrors `canAccessTeam` in the sidebar. Defaults to
   * `true` so private-workspace behavior is unchanged.
   */
  canManageWorkspace?: boolean;
}

export function WorkspaceSettingsMenu({
  triggerRef,
  portalContainer,
  setShowWorkspaceSettings,
  showCoworkers,
  onToggleCoworkers,
  canManageWorkspace = true,
}: WorkspaceSettingsMenuProps) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Settings"
          className="mr-[6px] p-1 text-foreground/80 transition-colors hover:text-foreground [-webkit-app-region:no-drag]"
        >
          <GearSix size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        container={portalContainer}
        className="[-webkit-app-region:no-drag]"
      >
        <DropdownMenuItem
          onSelect={() => setShowWorkspaceSettings(true)}
          className="cursor-pointer"
        >
          {canManageWorkspace ? (
            <>
              <GearSix size={16} className="mr-2" />
              Workspace Settings
            </>
          ) : (
            <>
              <SignOut size={16} className="mr-2" />
              Leave workspace
            </>
          )}
        </DropdownMenuItem>
        {canManageWorkspace && (
          <>
            <DropdownMenuItem
              onSelect={() => {
                if (!showCoworkers) onToggleCoworkers?.();
              }}
              className="cursor-pointer"
            >
              <UserPlus size={16} className="mr-2" />
              Invite and manage members
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                queueMicrotask(() => {
                  window.dispatchEvent(new CustomEvent("ano:team-open-skills"));
                });
              }}
              className="cursor-pointer"
            >
              <PlugsConnected size={16} className="mr-2" />
              Skills
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                queueMicrotask(() => {
                  window.dispatchEvent(new CustomEvent("ano:team-open-tools"));
                });
              }}
              className="cursor-pointer"
            >
              <Plugs size={16} className="mr-2" />
              Connected Tools
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
